import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CaretRightIcon, CaretDownIcon, XIcon } from '@phosphor-icons/react';

const LIST_ID = 'ModalPicker-selectedList';

const SelectedList = ({ selected, onRemove, fallbackFocusRef, i18nBase, renderItemExtras = null }) => {
  const [expanded, setExpanded] = useState(false);
  const itemRefs = useRef(new Map());
  const isEmpty = selected.length === 0;
  const showItems = expanded && !isEmpty;

  // When the user removes an entry via the keyboard, move focus to the next
  // remaining × button so they aren't stranded; fall back to the supplied focus
  // target (typically the search input) when the list has emptied.
  function handleRemove(item, index) {
    onRemove(item);

    const remaining = selected.filter((entry) => entry.id !== item.id);
    if (remaining.length === 0) {
      fallbackFocusRef?.current?.focus();
      return;
    }

    const nextIndex = Math.min(index, remaining.length - 1);
    const nextItem = remaining[nextIndex];
    const nextButton = itemRefs.current.get(nextItem.id);
    nextButton?.focus();
  }

  function handleToggle() {
    setExpanded((value) => !value);
  }

  const label = I18n.t(`${i18nBase}.modal.selected_label`);

  return (
    <div className="ModalPicker-selectedList">
      <button
        type="button"
        className="ModalPicker-selectedToggle"
        aria-expanded={showItems}
        aria-controls={LIST_ID}
        disabled={isEmpty}
        onClick={handleToggle}
      >
        <span aria-hidden className="ModalPicker-selectedChevron">
          {showItems ? <CaretDownIcon size={16} weight="bold" /> : <CaretRightIcon size={16} weight="bold" />}
        </span>
        <strong>{label}</strong>
      </button>
      {showItems && (
        <ul id={LIST_ID} className="ModalPicker-selectedItems">
          {selected.map((item, index) => (
            <li key={item.id} className="ModalPicker-selectedItem">
              <span className="ModalPicker-selectedItemName">{item.name}</span>
              {renderItemExtras && renderItemExtras(item)}
              <button
                ref={(node) => {
                  if (node) {
                    itemRefs.current.set(item.id, node);
                  } else {
                    itemRefs.current.delete(item.id);
                  }
                }}
                type="button"
                className="ModalPicker-selectedItemRemove"
                aria-label={I18n.t(`${i18nBase}.modal.remove_aria`, { name: item.name })}
                onClick={() => handleRemove(item, index)}
              >
                <XIcon size="16px" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

SelectedList.propTypes = {
  selected: PropTypes.array.isRequired,
  onRemove: PropTypes.func.isRequired,
  fallbackFocusRef: PropTypes.shape({ current: PropTypes.any }),
  i18nBase: PropTypes.string.isRequired,
  // Optional render-prop returning extra content rendered between each
  // selected item's name and its remove button (e.g. a per-item profile
  // link for researcher selections).
  renderItemExtras: PropTypes.func
};

export default SelectedList;
